"use client";

import { HeroSection } from "@/components/home/section-01-hero";
import { ExecutiveSummarySection } from "@/components/home/section-02-executive-summary";
import { ByTheNumbersSection } from "@/components/home/section-03-by-the-numbers";
import { TripleThreatSection } from "@/components/home/section-04-triple-threat";
import { AboutSection } from "@/components/home/section-05-about";
import { OrganizationSection } from "@/components/home/section-06-organization";
import { DifferentiatorsSection } from "@/components/home/section-07-differentiators";
import { ProblemSection } from "@/components/home/section-08-problem";
import { SolutionSection } from "@/components/home/section-09-solution";
import { MethodologySection } from "@/components/home/section-10-methodology";
import { PentaPsSection } from "@/components/home/section-12-penta-ps";
import { FocusAreasSection } from "@/components/home/section-13-focus-areas";
import { ServicesSection } from "@/components/home/section-14-services";
import { SpeakingTopicsSection } from "@/components/home/section-15-speaking-topics";
import { GenesisSection } from "@/components/home/section-16-genesis";
import { DoctrineSection } from "@/components/home/section-17-doctrine";
import { OperatingPrinciplesSection } from "@/components/home/section-18-operating-principles";
import { IdealTargetSection } from "@/components/home/section-19-ideal-target";
import { WhyChooseSection } from "@/components/home/section-20-why-choose";
import { SelectedWritingSection } from "@/components/home/section-21-selected-writing";
import { CircleOfTrustSection } from "@/components/home/section-22-circle-of-trust";
import { ComplianceSection } from "@/components/home/section-23-compliance";
import { FAQSection } from "@/components/home/section-24-faq";
import { PeopleAlsoAskSection } from "@/components/home/section-25-people-also-ask";
import { GoalsSection } from "@/components/home/section-26-goals";
import { BenefitsSection } from "@/components/home/section-27-benefits";
import { StrategicAccelerationSection } from "@/components/home/section-28-strategic-acceleration";
import { OperatorsNotebookSection } from "@/components/home/section-29-operators-notebook";
import { ContactSection } from "@/components/home/section-30-contact";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";
import { TabsBlock } from "@/lib/blocks/tabs-block";
import {
  Stethoscope,
  Activity,
  Target,
  Building2,
  Cpu,
  ShieldCheck,
  HeartPulse,
  Globe2,
  Mic,
  Library,
} from "lucide-react";
import Link from "next/link";

const LIBRARY_TABS = [
  {
    label: "Leadership",
    value: "leadership",
    icon: <Stethoscope className="size-3.5" />,
    content: (
      <div className="space-y-3">
        <div className="text-overline text-cyan mb-2">Leadership as Diagnosis</div>
        <h3 className="text-heading-lg mb-3">Leadership articles from the archive.</h3>
        <ul className="space-y-2">
          {[
            "Everything About Leadership, Learned in a Delivery Room",
            "Diagnose Before Treating — The Most Violated Rule",
            "Triage Under Pressure — Sequencing the Work When Everything Is Urgent",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2 text-body-sm text-muted-foreground">
              <span className="size-1.5 rounded-full bg-cyan mt-2 shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <Link href="/insights/leadership/" className="inline-flex items-center gap-1.5 text-caption font-semibold text-cyan mt-2 hover:gap-2.5 transition-all">
          Explore the Leadership archive
        </Link>
      </div>
    ),
  },
  {
    label: "Operations",
    value: "operations",
    icon: <Activity className="size-3.5" />,
    content: (
      <div className="space-y-3">
        <div className="text-overline text-cyan mb-2">Operational Excellence</div>
        <h3 className="text-heading-lg mb-3">Operations articles from the archive.</h3>
        <ul className="space-y-2">
          {[
            "Excellence Is an Operating System, Not a Value",
            "Run the Protocol; Keep the Judgment",
            "Own the Outcome, Not the Effort",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2 text-body-sm text-muted-foreground">
              <span className="size-1.5 rounded-full bg-cyan mt-2 shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <Link href="/insights/operations/" className="inline-flex items-center gap-1.5 text-caption font-semibold text-cyan mt-2 hover:gap-2.5 transition-all">
          Explore the Operations archive
        </Link>
      </div>
    ),
  },
  {
    label: "Strategy",
    value: "strategy",
    icon: <Target className="size-3.5" />,
    content: (
      <div className="space-y-3">
        <div className="text-overline text-cyan mb-2">Entrepreneurship &amp; Strategy</div>
        <h3 className="text-heading-lg mb-3">Strategy articles from the archive.</h3>
        <ul className="space-y-2">
          {[
            "Build in the Dark — The Operator&apos;s Discipline",
            "First, Do No Harm — The Most Underrated Leadership Skill",
            "The Operating System Is the Culture",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2 text-body-sm text-muted-foreground">
              <span className="size-1.5 rounded-full bg-cyan mt-2 shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <Link href="/insights/strategy/" className="inline-flex items-center gap-1.5 text-caption font-semibold text-cyan mt-2 hover:gap-2.5 transition-all">
          Explore the Strategy archive
        </Link>
      </div>
    ),
  },
  {
    label: "Policy",
    value: "policy",
    icon: <Building2 className="size-3.5" />,
    content: (
      <div className="space-y-3">
        <div className="text-overline text-cyan mb-2">Governance &amp; Public Policy</div>
        <h3 className="text-heading-lg mb-3">Policy articles from the archive.</h3>
        <ul className="space-y-2">
          {[
            "Good Governance Is Not a Slogan — It Is Engineered Delivery",
            "Why Governance Wins Elections",
            "What Governments Can Learn from Clinical Medicine",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2 text-body-sm text-muted-foreground">
              <span className="size-1.5 rounded-full bg-cyan mt-2 shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <Link href="/insights/policy/" className="inline-flex items-center gap-1.5 text-caption font-semibold text-cyan mt-2 hover:gap-2.5 transition-all">
          Explore the Policy archive
        </Link>
      </div>
    ),
  },
  {
    label: "Technology",
    value: "technology",
    icon: <Cpu className="size-3.5" />,
    content: (
      <div className="space-y-3">
        <div className="text-overline text-cyan mb-2">AI &amp; Technology</div>
        <h3 className="text-heading-lg mb-3">Technology articles from the archive.</h3>
        <ul className="space-y-2">
          {[
            "The Architecture of the Systems That Defend and Compete",
            "Zero-Trust Architecture as the Operating Standard",
            "Why Political-Grade Stakes Demand Clinical-Grade Discipline",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2 text-body-sm text-muted-foreground">
              <span className="size-1.5 rounded-full bg-cyan mt-2 shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <Link href="/insights/technology/" className="inline-flex items-center gap-1.5 text-caption font-semibold text-cyan mt-2 hover:gap-2.5 transition-all">
          Explore the Technology archive
        </Link>
      </div>
    ),
  },
  {
    label: "Security",
    value: "security",
    icon: <ShieldCheck className="size-3.5" />,
    content: (
      <div className="space-y-3">
        <div className="text-overline text-cyan mb-2">Crisis &amp; Security</div>
        <h3 className="text-heading-lg mb-3">Security articles from the archive.</h3>
        <ul className="space-y-2">
          {[
            "Crisis as a Discipline, Not an Improvisation",
            "The Most Resilient Organizations Rehearse",
            "Composure as Infrastructure — The Operator&apos;s Discipline",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2 text-body-sm text-muted-foreground">
              <span className="size-1.5 rounded-full bg-cyan mt-2 shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <Link href="/insights/security/" className="inline-flex items-center gap-1.5 text-caption font-semibold text-cyan mt-2 hover:gap-2.5 transition-all">
          Explore the Security archive
        </Link>
      </div>
    ),
  },
  {
    label: "Women",
    value: "women",
    icon: <HeartPulse className="size-3.5" />,
    content: (
      <div className="space-y-3">
        <div className="text-overline text-cyan mb-2">Women in Leadership</div>
        <h3 className="text-heading-lg mb-3">Women-led operations articles from the archive.</h3>
        <ul className="space-y-2">
          {[
            "Why Teams Built Seventy Percent Women",
            "The Operating Case — Not the Moral Case",
            "Hire the Talent Others Overlook",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2 text-body-sm text-muted-foreground">
              <span className="size-1.5 rounded-full bg-cyan mt-2 shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <Link href="/insights/women/" className="inline-flex items-center gap-1.5 text-caption font-semibold text-cyan mt-2 hover:gap-2.5 transition-all">
          Explore the Women archive
        </Link>
      </div>
    ),
  },
  {
    label: "Global",
    value: "global",
    icon: <Globe2 className="size-3.5" />,
    content: (
      <div className="space-y-3">
        <div className="text-overline text-cyan mb-2">Global Operations</div>
        <h3 className="text-heading-lg mb-3">Global operations articles from the archive.</h3>
        <ul className="space-y-2">
          {[
            "Sovereign-Scale Operations Across Continents and Cultures",
            "Discretion as the Operating Advantage Most Forfeit",
            "Cross-Jurisdiction Compliance as an Operating Discipline",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2 text-body-sm text-muted-foreground">
              <span className="size-1.5 rounded-full bg-cyan mt-2 shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <Link href="/insights/global/" className="inline-flex items-center gap-1.5 text-caption font-semibold text-cyan mt-2 hover:gap-2.5 transition-all">
          Explore the Global archive
        </Link>
      </div>
    ),
  },
  {
    label: "Voice",
    value: "voice",
    icon: <Mic className="size-3.5" />,
    content: (
      <div className="space-y-3">
        <div className="text-overline text-cyan mb-2">Perception &amp; Reputation</div>
        <h3 className="text-heading-lg mb-3">Voice and perception articles from the archive.</h3>
        <ul className="space-y-2">
          {[
            "Narrative Engineering at the Speed of Digital",
            "The Voice of an Organization as a Defensible Asset",
            "Perception Is Engineered, Not Managed",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2 text-body-sm text-muted-foreground">
              <span className="size-1.5 rounded-full bg-cyan mt-2 shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <Link href="/insights/voice/" className="inline-flex items-center gap-1.5 text-caption font-semibold text-cyan mt-2 hover:gap-2.5 transition-all">
          Explore the Voice archive
        </Link>
      </div>
    ),
  },
];

export default function HomePage() {
  return (
    <>
      <HeroSection />
      <ExecutiveSummarySection />
      <ByTheNumbersSection />
      <TripleThreatSection />
      <AboutSection />
      <OrganizationSection />
      <DifferentiatorsSection />
      <ProblemSection />
      <SolutionSection />
      <MethodologySection />
      <PentaPsSection />
      <FocusAreasSection />
      <ServicesSection />
      <SpeakingTopicsSection />
      <GenesisSection />
      <DoctrineSection />
      <OperatingPrinciplesSection />
      <IdealTargetSection />
      <WhyChooseSection />
      <SelectedWritingSection />
      <CircleOfTrustSection />
      <ComplianceSection />
      <FAQSection />
      <PeopleAlsoAskSection />
      <GoalsSection />
      <BenefitsSection />
      <StrategicAccelerationSection />
      <OperatorsNotebookSection />
      <Section id="operating-library" ariaLabel="The Operating Library" background="muted" className="py-16 lg:py-20">
        <SectionHeader
          icon={Library}
          overline="The Operating Library"
          overlineClassName="text-cyan"
          pillClassName="glass-tinted-cyan border-cyan/20"
          title={
            <>
              Nine insights domains. <span className="text-gradient-premium">One operating method.</span>
            </>
          }
          subtitle="The full body of thought leadership — the clinical method applied to organizations. Each domain is a domain of practice, organized for leaders who want principles they can act on."
        />
        <TabsBlock tabs={LIBRARY_TABS} defaultValue="leadership" />
      </Section>
      <ContactSection />
    </>
  );
}
